import type React from 'react';
import type { BookDefaultStylesV1, ContentAlign } from './bookDefaults';
import { mergeBookDefaultStyles, parseContentAlign } from './bookDefaults';
import { cleanTagLineStyles } from './tagLineStyles';

export type { ContentAlign } from './bookDefaults';

export type BookDefaultStylesFormValues = {
  pagePaddingPx?: number;
  baseFontSizePx?: number;
  gridGapPx?: number;
  contentLineGapPx?: number;
  contentAlign?: ContentAlign;
  defaultTextColor?: string;
  defaultBackgroundColor?: string;
  defaultLinkColor?: string;
  defaultHeaderStyle?: any;
  defaultLineStyle?: any;
  defaultNestedLineStyle?: any;
  defaultDeepNestedLineStyle?: any;
  defaultLinkStyle?: any;
  tagLineStyles?: Record<string, unknown>;
  tagStyleDraft?: any;
};

export function asPreviewCss(s: any): React.CSSProperties {
  if (!s || typeof s !== 'object') return {};
  const textAlign = s.align || s.textAlign;
  const borderWidth = Number.isFinite(Number(s.borderWidth)) ? Number(s.borderWidth) : 0;
  const borderStyle = s.borderStyle;
  const borderColor = typeof s.borderColor === 'string' ? s.borderColor : undefined;
  const border =
    borderStyle && borderStyle !== 'none' && borderWidth > 0
      ? `${borderWidth}px ${borderStyle} ${borderColor || (typeof s.color === 'string' ? s.color : '#d9d9d9')}`
      : undefined;
  const padding =
    s.paddingLeft || s.paddingRight || s.paddingTop || s.paddingBottom
      ? `${Number(s.paddingTop || 0)}px ${Number(s.paddingRight || 0)}px ${Number(s.paddingBottom || 0)}px ${Number(s.paddingLeft || 0)}px`
      : Number.isFinite(Number(s.padding)) && Number(s.padding) > 0
        ? Number(s.padding)
        : undefined;
  const margin =
    s.marginLeft || s.marginRight || s.marginTop || s.marginBottom
      ? `${Number(s.marginTop || 0)}px ${Number(s.marginRight || 0)}px ${Number(s.marginBottom || 0)}px ${Number(s.marginLeft || 0)}px`
      : Number.isFinite(Number(s.margin)) && Number(s.margin) > 0
        ? Number(s.margin)
        : undefined;
  return {
    fontSize: Number.isFinite(Number(s.fontSize)) ? Number(s.fontSize) : undefined,
    color: typeof s.color === 'string' ? s.color : undefined,
    backgroundColor: typeof s.backgroundColor === 'string' ? s.backgroundColor : undefined,
    fontFamily: typeof s.fontFamily === 'string' ? s.fontFamily : undefined,
    fontWeight: Number.isFinite(Number(s.fontWeight))
      ? Number(s.fontWeight)
      : s.bold
        ? 700
        : undefined,
    fontStyle: s.italic ? 'italic' : undefined,
    textDecoration: s.underline ? 'underline' : undefined,
    textAlign: textAlign === 'left' || textAlign === 'center' || textAlign === 'right' || textAlign === 'justify' ? textAlign : undefined,
    border,
    borderRadius: Number.isFinite(Number(s.borderRadius)) ? Number(s.borderRadius) : undefined,
    padding,
    margin,
    display: 'inline-block',
    maxWidth: '100%',
    whiteSpace: 'pre-wrap',
  };
}

export function bookDefaultsToFormValues(cur: BookDefaultStylesV1): BookDefaultStylesFormValues {
  return {
    pagePaddingPx: cur.pagePaddingPx,
    baseFontSizePx: cur.baseFontSizePx,
    gridGapPx: cur.gridGapPx,
    contentLineGapPx: cur.contentLineGapPx ?? 0,
    contentAlign: cur.contentAlign ?? 'left',
    defaultTextColor: cur.defaultTextColor || '',
    defaultBackgroundColor: cur.defaultBackgroundColor || '',
    defaultLinkColor: cur.defaultLinkColor || '',
    defaultHeaderStyle: {
      ...(cur.defaultHeaderStyle || {}),
      bold: (((cur.defaultHeaderStyle as any)?.fontWeight ?? 600) >= 600),
      italic: (cur.defaultHeaderStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultHeaderStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultHeaderStyle as any)?.textAlign || 'left',
    },
    defaultLineStyle: {
      ...(cur.defaultLineStyle || {}),
      bold: (((cur.defaultLineStyle as any)?.fontWeight ?? 400) >= 600),
      italic: (cur.defaultLineStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultLineStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultLineStyle as any)?.textAlign || 'left',
    },
    defaultNestedLineStyle: {
      ...(cur.defaultNestedLineStyle || {}),
      bold: (((cur.defaultNestedLineStyle as any)?.fontWeight ?? 400) >= 600),
      italic: (cur.defaultNestedLineStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultNestedLineStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultNestedLineStyle as any)?.textAlign || 'left',
    },
    defaultDeepNestedLineStyle: {
      ...(cur.defaultDeepNestedLineStyle || {}),
      bold: (((cur.defaultDeepNestedLineStyle as any)?.fontWeight ?? 400) >= 600),
      italic: (cur.defaultDeepNestedLineStyle as any)?.fontStyle === 'italic',
      underline: String((cur.defaultDeepNestedLineStyle as any)?.textDecoration || '').includes('underline'),
      align: (cur.defaultDeepNestedLineStyle as any)?.textAlign || 'left',
    },
    defaultLinkStyle: {
      ...(cur.defaultLinkStyle || {}),
      align: (cur.defaultLinkStyle as any)?.textAlign || 'left',
    },
    tagLineStyles: cur.tagLineStyles ? { ...cur.tagLineStyles } : {},
  };
}

/** Merge live form values onto the selected template so unmounted tabs (e.g. Book defaults) are not lost on save. */
export function mergeBookDefaultsFormValues(
  formVals: BookDefaultStylesFormValues,
  base: BookDefaultStylesV1,
): BookDefaultStylesFormValues {
  const baseForm = bookDefaultsToFormValues(base);
  const mergeStyle = (baseStyle: any, patch: any) =>
    patch != null && typeof patch === 'object' ? { ...baseStyle, ...patch } : baseStyle;

  return {
    ...baseForm,
    ...formVals,
    defaultHeaderStyle: mergeStyle(baseForm.defaultHeaderStyle, formVals.defaultHeaderStyle),
    defaultLineStyle: mergeStyle(baseForm.defaultLineStyle, formVals.defaultLineStyle),
    defaultNestedLineStyle: mergeStyle(baseForm.defaultNestedLineStyle, formVals.defaultNestedLineStyle),
    defaultDeepNestedLineStyle: mergeStyle(
      baseForm.defaultDeepNestedLineStyle,
      formVals.defaultDeepNestedLineStyle,
    ),
    defaultLinkStyle: mergeStyle(baseForm.defaultLinkStyle, formVals.defaultLinkStyle),
    tagLineStyles:
      formVals.tagLineStyles != null && typeof formVals.tagLineStyles === 'object'
        ? { ...(formVals.tagLineStyles as Record<string, unknown>) }
        : baseForm.tagLineStyles,
  };
}

export function formValuesToBookDefaults(vals: any, base: BookDefaultStylesV1): BookDefaultStylesV1 {
  const cleanStringOrUndef = (v: any) => (typeof v === 'string' && v.trim() ? v.trim() : undefined);
  const cleanNumberOrUndef = (v: any) => (v != null && Number.isFinite(Number(v)) ? Number(v) : undefined);
  const cleanPositiveOrUndef = (v: any) => {
    const n = cleanNumberOrUndef(v);
    return n != null && n > 0 ? n : undefined;
  };
  const cleanStyle = (s: any, mode: 'header' | 'line') => {
    if (!s || typeof s !== 'object') return undefined;
    const align = s.align ?? s.textAlign;
    const span = s.span === 12 || s.span === 24 ? s.span : undefined;
    const baseStyle: any = {
      fontSize: cleanPositiveOrUndef(s.fontSize),
      color: cleanStringOrUndef(s.color),
      backgroundColor: cleanStringOrUndef(s.backgroundColor),
      fontFamily: cleanStringOrUndef(s.fontFamily),
      fontWeight:
        mode === 'line'
          ? 'bold' in s
            ? s.bold
              ? 700
              : 400
            : cleanNumberOrUndef(s.fontWeight) ?? undefined
          : cleanNumberOrUndef(s.fontWeight) ??
            ('bold' in s ? (s.bold ? 700 : 400) : undefined),
      fontStyle:
        'italic' in s
          ? s.italic
            ? ('italic' as const)
            : ('normal' as const)
          : s.italic
            ? ('italic' as const)
            : undefined,
      textDecoration:
        'underline' in s
          ? s.underline
            ? 'underline'
            : 'none'
          : s.underline
            ? 'underline'
            : undefined,
      textAlign:
        align === 'left' || align === 'center' || align === 'right' || align === 'justify'
          ? align
          : undefined,
      borderWidth: cleanPositiveOrUndef(s.borderWidth),
      borderStyle:
        s.borderStyle === 'solid' || s.borderStyle === 'dashed' || s.borderStyle === 'dotted' || s.borderStyle === 'double'
          ? s.borderStyle
          : undefined,
      borderColor: cleanStringOrUndef(s.borderColor),
      borderRadius: cleanPositiveOrUndef(s.borderRadius),
      padding: cleanPositiveOrUndef(s.padding),
      paddingLeft: cleanPositiveOrUndef(s.paddingLeft),
      paddingRight: cleanPositiveOrUndef(s.paddingRight),
      paddingTop: cleanPositiveOrUndef(s.paddingTop),
      paddingBottom: cleanPositiveOrUndef(s.paddingBottom),
      margin: cleanPositiveOrUndef(s.margin),
      marginLeft: cleanPositiveOrUndef(s.marginLeft),
      marginRight: cleanPositiveOrUndef(s.marginRight),
      marginTop: cleanPositiveOrUndef(s.marginTop),
      marginBottom: cleanPositiveOrUndef(s.marginBottom),
    };
    if (mode === 'line' && span) baseStyle.span = span;
    return baseStyle;
  };

  const numOr = (v: any, fallback: number) => {
    const n = Number(v);
    return Number.isFinite(n) ? n : fallback;
  };
  const strOr = (v: any, fallback: string) => (typeof v === 'string' ? v : fallback);

  const mergeCleanedStyle = (
    baseStyle: Record<string, unknown> | undefined,
    formStyle: unknown,
    mode: 'header' | 'line',
  ) => {
    if (formStyle == null) return baseStyle;
    const cleaned = cleanStyle(formStyle, mode);
    if (!cleaned || Object.keys(cleaned).length === 0) return baseStyle;
    if (!baseStyle) return cleaned;
    const merged = { ...baseStyle };
    for (const [key, value] of Object.entries(cleaned)) {
      if (value !== undefined) {
        merged[key] = value;
      }
    }
    if (mode === 'line' && 'span' in (formStyle as object)) {
      const formSpan = (formStyle as { span?: unknown }).span;
      if (formSpan !== 12 && formSpan !== 24) {
        delete merged.span;
      }
    }
    return merged;
  };

  return {
    version: 1 as const,
    pagePaddingPx: numOr(vals.pagePaddingPx, base.pagePaddingPx),
    baseFontSizePx: numOr(vals.baseFontSizePx, base.baseFontSizePx),
    gridGapPx: numOr(vals.gridGapPx, base.gridGapPx),
    contentLineGapPx: Math.max(0, numOr(vals.contentLineGapPx, base.contentLineGapPx ?? 0)),
    contentAlign: parseContentAlign(vals.contentAlign, parseContentAlign(base.contentAlign, 'left')),
    // Not editable in UI today; preserve.
    headerBackgroundColor: base.headerBackgroundColor || '',
    headerTextColor: base.headerTextColor || '',
    titleColor: base.titleColor || '',
    defaultTextColor: strOr(vals.defaultTextColor, base.defaultTextColor || ''),
    defaultBackgroundColor: strOr(vals.defaultBackgroundColor, base.defaultBackgroundColor || ''),
    defaultLinkColor: strOr(vals.defaultLinkColor, base.defaultLinkColor || ''),
    defaultHeaderStyle: mergeCleanedStyle(
      base.defaultHeaderStyle as Record<string, unknown> | undefined,
      vals.defaultHeaderStyle,
      'header',
    ) as BookDefaultStylesV1['defaultHeaderStyle'],
    defaultLineStyle: mergeCleanedStyle(
      base.defaultLineStyle as Record<string, unknown> | undefined,
      vals.defaultLineStyle,
      'line',
    ) as BookDefaultStylesV1['defaultLineStyle'],
    defaultNestedLineStyle: mergeCleanedStyle(
      base.defaultNestedLineStyle as Record<string, unknown> | undefined,
      vals.defaultNestedLineStyle,
      'line',
    ) as BookDefaultStylesV1['defaultNestedLineStyle'],
    defaultDeepNestedLineStyle: mergeCleanedStyle(
      base.defaultDeepNestedLineStyle as Record<string, unknown> | undefined,
      vals.defaultDeepNestedLineStyle,
      'line',
    ) as BookDefaultStylesV1['defaultDeepNestedLineStyle'],
    // Not editable in UI today; preserve.
    defaultCardHeaderStyle: base.defaultCardHeaderStyle,
    defaultCardTitleStyle: base.defaultCardTitleStyle,
    defaultTagStyle: base.defaultTagStyle,
    defaultLinkStyle: mergeCleanedStyle(
      base.defaultLinkStyle as Record<string, unknown> | undefined,
      vals.defaultLinkStyle,
      'header',
    ) as BookDefaultStylesV1['defaultLinkStyle'],
    defaultGranthaReferenceStyle: base.defaultGranthaReferenceStyle,
    defaultSankalpaBlockStyle: base.defaultSankalpaBlockStyle,
    defaultBhedStyle: base.defaultBhedStyle,
    gridColumns: vals.gridColumns === 2 ? 2 : (base.gridColumns ?? 1),
    tagLineStyles:
      vals.tagLineStyles != null && typeof vals.tagLineStyles === 'object'
        ? cleanTagLineStyles(vals.tagLineStyles)
        : cleanTagLineStyles(vals.tagLineStyles) ?? base.tagLineStyles,
    ...(base.bookOverrides && typeof base.bookOverrides === 'object'
      ? { bookOverrides: { ...base.bookOverrides } }
      : {}),
  };
}

export type StyleTemplatePayload = BookDefaultStylesV1 & {
  bookOverrides?: Record<string, unknown>;
};

function cloneBookOverrides(source: StyleTemplatePayload | BookDefaultStylesV1 | undefined) {
  if (!source?.bookOverrides || typeof source.bookOverrides !== 'object') return undefined;
  return { ...(source.bookOverrides as Record<string, unknown>) };
}

/** Build API template styles from form draft while preserving per-book overrides. */
export function buildStyleTemplatePayload(
  formVals: BookDefaultStylesFormValues,
  sourceStyles: StyleTemplatePayload,
  options?: {
    bookOverridePatches?: Record<string, Record<string, unknown>>;
    saveAsNew?: boolean;
  },
): StyleTemplatePayload {
  const merged = mergeBookDefaultsFormValues(formVals, sourceStyles);
  const draft = formValuesToBookDefaults(merged, sourceStyles);

  if (options?.saveAsNew) {
    const { bookOverrides: _removed, ...templateOnly } = draft;
    return templateOnly;
  }

  const bookOverrides: Record<string, unknown> = { ...(cloneBookOverrides(sourceStyles) ?? {}) };

  if (options?.bookOverridePatches) {
    for (const [bookKey, patch] of Object.entries(options.bookOverridePatches)) {
      const existing =
        bookOverrides[bookKey] && typeof bookOverrides[bookKey] === 'object'
          ? (bookOverrides[bookKey] as Record<string, unknown>)
          : {};
      bookOverrides[bookKey] = { ...existing, ...patch };
    }
  }

  if (Object.keys(bookOverrides).length === 0) return draft;
  return { ...draft, bookOverrides };
}

/** Factory-reset template styles (optionally keeping per-book overrides when editing an existing template). */
export function createClearedBookDefaultsTemplate(options?: {
  preserveBookOverrides?: Record<string, unknown>;
}): StyleTemplatePayload {
  const cleared = mergeBookDefaultStyles(null);
  const overrides = options?.preserveBookOverrides;
  if (overrides && typeof overrides === 'object' && Object.keys(overrides).length > 0) {
    return { ...cleared, bookOverrides: { ...overrides } };
  }
  return cleared;
}

/** Merge base used when building a save payload; new templates start from factory defaults, not the selected template. */
export function resolveStyleTemplateSaveBase(options: {
  saveAsNew: boolean;
  selectedStyleTemplateBase: StyleTemplatePayload;
}): StyleTemplatePayload {
  if (options.saveAsNew) {
    return mergeBookDefaultStyles(null);
  }
  return options.selectedStyleTemplateBase;
}

export function buildClearedBookDefaultsEditorState(options: {
  templateName?: string;
  saveAsNew?: boolean;
  preserveBookOverrides?: Record<string, unknown>;
}): { clearedTemplate: StyleTemplatePayload; formValues: Record<string, unknown> } {
  const saveAsNew = !!options.saveAsNew;
  const preserveOverrides = saveAsNew ? undefined : options.preserveBookOverrides;
  let clearedTemplate = createClearedBookDefaultsTemplate(
    preserveOverrides && Object.keys(preserveOverrides).length > 0
      ? { preserveBookOverrides: preserveOverrides }
      : undefined,
  );
  if (saveAsNew) {
    const { bookOverrides: _removed, tagLineStyles: _tags, ...factoryTemplate } = clearedTemplate;
    clearedTemplate = factoryTemplate;
  }
  return {
    clearedTemplate,
    formValues: {
      ...bookDefaultsToFormValues(clearedTemplate),
      templateName: options.templateName ?? '',
      saveAsNew,
      tagLineStyles: {},
      tagStyleDraft: {},
    },
  };
}

/** When “Save as new” is enabled, drop inherited template tags/overrides from the in-memory draft. */
export function stripTemplateDraftForSaveAsNew(options: {
  form: { getFieldValue: (name: string) => unknown; setFieldsValue: (values: Record<string, unknown>) => void };
  setSelectedStyleTemplateBase: (base: StyleTemplatePayload) => void;
  preserveTemplateName?: boolean;
}): void {
  const fresh = mergeBookDefaultStyles(null);
  const templateName = options.preserveTemplateName
    ? String(options.form.getFieldValue('templateName') ?? '')
    : '';
  options.setSelectedStyleTemplateBase(fresh);
  options.form.setFieldsValue({
    tagLineStyles: {},
    tagStyleDraft: {},
    saveAsNew: true,
    ...(templateName ? { templateName } : {}),
  });
}

/** Apply a full template reset to the editor form and in-memory draft. */
export function applyClearedBookDefaultsEditorState(options: {
  form: {
    getFieldValue: (name: string) => unknown;
    resetFields: () => void;
    setFieldsValue: (values: Record<string, unknown>) => void;
  };
  setSelectedStyleTemplateBase: (base: StyleTemplatePayload) => void;
  templateName?: string;
  saveAsNew?: boolean;
  preserveBookOverrides?: Record<string, unknown>;
}): StyleTemplatePayload {
  const { clearedTemplate, formValues } = buildClearedBookDefaultsEditorState({
    templateName: options.templateName ?? String(options.form.getFieldValue('templateName') ?? ''),
    saveAsNew: options.saveAsNew ?? !!options.form.getFieldValue('saveAsNew'),
    preserveBookOverrides: options.preserveBookOverrides,
  });
  options.setSelectedStyleTemplateBase(clearedTemplate);
  options.form.resetFields();
  options.form.setFieldsValue(formValues);
  return clearedTemplate;
}

